// react-native-maps for the web, built on the Google Maps JavaScript API.
//
// Covers what the app uses: MapView (initialRegion/region, onRegionChangeComplete,
// onPanDrag, onPress, showsUserLocation + onUserLocationChange, and the
// animateToRegion / animateCamera / fitToCoordinates / getCamera ref methods),
// Marker (default pin, or custom React children with anchor/zIndex/onPress and
// showCallout/hideCallout), Callout, CalloutSubview, Polyline, Circle and Polygon.
//
// Uses the same GOOGLE_MAPS_API_KEY as the rest of the app. The key needs the
// "Maps JavaScript API" enabled in Google Cloud Console.
import React, {
  createContext,
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {createPortal} from 'react-dom';
import {Pressable, StyleSheet, Text, View} from 'react-native-web';
import {GOOGLE_MAPS_API_KEY} from '../../src/config/apis';

export const PROVIDER_GOOGLE = 'google';
export const PROVIDER_DEFAULT = undefined;
export const MAP_TYPES = {
  STANDARD: 'standard',
  SATELLITE: 'satellite',
  HYBRID: 'hybrid',
  TERRAIN: 'terrain',
};

// --- Loading the Maps JavaScript API ---------------------------------------

let mapsPromise = null;
const loadGoogleMaps = () => {
  if (!mapsPromise) {
    mapsPromise = new Promise((resolve, reject) => {
      if (window.google?.maps?.Map) {
        return resolve(window.google.maps);
      }
      const callback = '__pompiMapsLoaded';
      window[callback] = async () => {
        try {
          await Promise.all([
            window.google.maps.importLibrary('maps'),
            window.google.maps.importLibrary('marker'),
          ]);
          resolve(window.google.maps);
        } catch (e) {
          reject(e);
        }
      };
      // Called by Google when the key is rejected (API not enabled, referrer
      // not allowed, billing...). The map would otherwise stay grey.
      window.gm_authFailure = () => {
        authFailed = true;
        authListeners.forEach(fn => fn());
      };
      const params = new URLSearchParams({
        key: GOOGLE_MAPS_API_KEY,
        v: 'weekly',
        loading: 'async',
        callback,
      });
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?${params}`;
      script.async = true;
      script.onerror = () => {
        mapsPromise = null;
        reject(new Error('Could not load Google Maps'));
      };
      document.head.appendChild(script);
    });
  }
  return mapsPromise;
};
let authFailed = false;
const authListeners = new Set();

// --- Region / camera helpers -------------------------------------------------

const toLatLng = c => ({lat: Number(c.latitude), lng: Number(c.longitude)});
const fromLatLng = ll => ({latitude: ll.lat(), longitude: ll.lng()});
const isValidCoord = c =>
  c &&
  Number.isFinite(Number(c.latitude)) &&
  Number.isFinite(Number(c.longitude));

const regionBounds = r => ({
  north: r.latitude + r.latitudeDelta / 2,
  south: r.latitude - r.latitudeDelta / 2,
  east: r.longitude + r.longitudeDelta / 2,
  west: r.longitude - r.longitudeDelta / 2,
});

// Zoom level that shows `longitudeDelta` degrees across `widthPx` pixels.
const zoomForRegion = (r, widthPx) =>
  Math.max(
    1,
    Math.min(21, Math.log2((widthPx * 360) / (256 * r.longitudeDelta))),
  );

const mapRegion = map => {
  const bounds = map.getBounds();
  const center = map.getCenter();
  if (!bounds || !center) {
    return null;
  }
  const ne = bounds.getNorthEast();
  const sw = bounds.getSouthWest();
  let lngDelta = ne.lng() - sw.lng();
  if (lngDelta < 0) {
    lngDelta += 360;
  }
  return {
    latitude: center.lat(),
    longitude: center.lng(),
    latitudeDelta: ne.lat() - sw.lat(),
    longitudeDelta: lngDelta,
  };
};

const sameRegion = (a, b) =>
  a &&
  b &&
  ['latitude', 'longitude', 'latitudeDelta', 'longitudeDelta'].every(
    k => Math.abs((a[k] ?? 0) - (b[k] ?? 0)) < 1e-7,
  );

const padding = p => ({
  top: p?.top ?? 0,
  right: p?.right ?? 0,
  bottom: p?.bottom ?? 0,
  left: p?.left ?? 0,
});

// --- MapView -------------------------------------------------------------------

const MapContext = createContext(null);

const MapView = forwardRef(function MapView(props, ref) {
  const {
    style,
    children,
    initialRegion,
    region,
    mapType,
    showsUserLocation,
    showsPointsOfInterest = true,
    zoomEnabled = true,
    scrollEnabled = true,
    rotateEnabled = true,
    zoomControlEnabled,
    minZoomLevel,
    maxZoomLevel,
  } = props;

  const containerRef = useRef(null);
  const [map, setMap] = useState(null);
  const [error, setError] = useState(authFailed ? 'auth' : null);
  const [userLocation, setUserLocation] = useState(null);
  const lastRegion = useRef(null);
  // True once the user or a ref method has moved the camera.
  const cameraMoved = useRef(false);

  // Latest callbacks, so map listeners never go stale.
  const handlers = useRef(props);
  handlers.current = props;

  useEffect(() => {
    const onAuth = () => setError('auth');
    authListeners.add(onAuth);
    let cancelled = false;
    loadGoogleMaps()
      .then(maps => {
        if (cancelled || !containerRef.current) {
          return;
        }
        const start = region || initialRegion;
        const width = containerRef.current.clientWidth || 400;
        const instance = new maps.Map(containerRef.current, {
          center: start ? toLatLng(start) : {lat: 52.1326, lng: 5.2913},
          zoom: start ? zoomForRegion(start, width) : 7,
          disableDefaultUI: true,
          zoomControl: Boolean(zoomControlEnabled),
          clickableIcons: showsPointsOfInterest,
          gestureHandling: scrollEnabled || zoomEnabled ? 'greedy' : 'none',
          keyboardShortcuts: false,
          isFractionalZoomEnabled: true,
          minZoom: minZoomLevel,
          maxZoom: maxZoomLevel,
          mapTypeId: mapType && mapType !== 'standard' ? mapType : 'roadmap',
          rotateControl: rotateEnabled,
          styles: showsPointsOfInterest
            ? undefined
            : [{featureType: 'poi', stylers: [{visibility: 'off'}]}],
        });
        const listeners = [
          instance.addListener('idle', () => {
            const r = mapRegion(instance);
            if (!r) {
              return;
            }
            lastRegion.current = r;
            handlers.current.onRegionChangeComplete?.(r, {isGesture: false});
          }),
          instance.addListener('bounds_changed', () => {
            if (!handlers.current.onRegionChange) {
              return;
            }
            const r = mapRegion(instance);
            if (r) {
              handlers.current.onRegionChange(r, {isGesture: false});
            }
          }),
          instance.addListener('dragstart', () => {
            cameraMoved.current = true;
          }),
          instance.addListener('drag', () =>
            handlers.current.onPanDrag?.({nativeEvent: {}}),
          ),
          instance.addListener('click', e => {
            if (e.placeId && !showsPointsOfInterest) {
              e.stop();
            }
            const coordinate = fromLatLng(e.latLng);
            handlers.current.onPress?.({
              nativeEvent: {coordinate, action: 'press'},
            });
          }),
          instance.addListener('contextmenu', e =>
            handlers.current.onLongPress?.({
              nativeEvent: {coordinate: fromLatLng(e.latLng)},
            }),
          ),
        ];
        instance.__pompiListeners = listeners;
        setMap(instance);
        handlers.current.onMapReady?.();
      })
      .catch(() => !cancelled && setError('load'));
    return () => {
      cancelled = true;
      authListeners.delete(onAuth);
    };
    // The map is created once; later prop changes are applied below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => () => map?.__pompiListeners?.forEach(l => l.remove()), [map]);

  // On the phone the starting region is applied once the map has its real
  // size. On the web the container often grows after the map is created, so
  // fit it again on resize until someone moves the camera.
  useEffect(() => {
    if (!map || !containerRef.current) {
      return;
    }
    const observer = new ResizeObserver(() => {
      const start = handlers.current.region || handlers.current.initialRegion;
      if (!cameraMoved.current && start) {
        map.fitBounds(regionBounds(start), 0);
      }
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [map]);

  // Controlled `region`: follow it when it changes.
  useEffect(() => {
    if (!map || !region || sameRegion(region, lastRegion.current)) {
      return;
    }
    map.fitBounds(regionBounds(region), 0);
  }, [map, region]);

  useEffect(() => {
    if (!map) {
      return;
    }
    map.setOptions({
      zoomControl: Boolean(zoomControlEnabled),
      clickableIcons: showsPointsOfInterest,
      minZoom: minZoomLevel,
      maxZoom: maxZoomLevel,
    });
  }, [map, zoomControlEnabled, showsPointsOfInterest, minZoomLevel, maxZoomLevel]);

  // The browser's version of the blue "my location" dot.
  useEffect(() => {
    if (!showsUserLocation || !navigator.geolocation) {
      return;
    }
    const id = navigator.geolocation.watchPosition(
      ({coords}) => {
        const coordinate = {
          latitude: coords.latitude,
          longitude: coords.longitude,
          accuracy: coords.accuracy,
          heading: coords.heading,
          speed: coords.speed,
          timestamp: Date.now(),
        };
        setUserLocation(coordinate);
        handlers.current.onUserLocationChange?.({nativeEvent: {coordinate}});
      },
      () => {},
      {enableHighAccuracy: true, maximumAge: 10000},
    );
    return () => navigator.geolocation.clearWatch(id);
  }, [showsUserLocation]);

  useImperativeHandle(
    ref,
    () => ({
      animateToRegion(r) {
        cameraMoved.current = true;
        if (map && r) {
          map.fitBounds(regionBounds(r), 0);
        }
      },
      animateCamera(camera = {}) {
        cameraMoved.current = true;
        if (!map) {
          return;
        }
        if (camera.center) {
          map.panTo(toLatLng(camera.center));
        }
        if (camera.zoom != null) {
          map.setZoom(camera.zoom);
        }
        if (camera.heading != null) {
          map.setHeading?.(camera.heading);
        }
      },
      setCamera(camera = {}) {
        cameraMoved.current = true;
        if (!map) {
          return;
        }
        if (camera.center) {
          map.setCenter(toLatLng(camera.center));
        }
        if (camera.zoom != null) {
          map.setZoom(camera.zoom);
        }
      },
      async getCamera() {
        const center = map?.getCenter();
        return {
          center: center ? fromLatLng(center) : null,
          zoom: map?.getZoom(),
          heading: map?.getHeading?.() || 0,
          pitch: map?.getTilt?.() || 0,
          altitude: 0,
        };
      },
      fitToCoordinates(coordinates = [], {edgePadding} = {}) {
        cameraMoved.current = true;
        if (!map || !coordinates.length) {
          return;
        }
        const bounds = new window.google.maps.LatLngBounds();
        coordinates
          .filter(isValidCoord)
          .forEach(c => bounds.extend(toLatLng(c)));
        map.fitBounds(bounds, padding(edgePadding));
      },
      fitToElements(options) {
        this.fitToCoordinates(markerCoords(map), options);
      },
      fitToSuppliedMarkers(_ids, options) {
        this.fitToCoordinates(markerCoords(map), options);
      },
      async getMapBoundaries() {
        const b = map?.getBounds();
        return b
          ? {
              northEast: fromLatLng(b.getNorthEast()),
              southWest: fromLatLng(b.getSouthWest()),
            }
          : null;
      },
      async pointForCoordinate(c) {
        const projection = overlayProjection(map);
        const p = projection?.fromLatLngToContainerPixel(toLatLng(c));
        return p ? {x: p.x, y: p.y} : {x: 0, y: 0};
      },
      async coordinateForPoint({x, y}) {
        const projection = overlayProjection(map);
        const ll = projection?.fromContainerPixelToLatLng(
          new window.google.maps.Point(x, y),
        );
        return ll ? fromLatLng(ll) : null;
      },
    }),
    [map],
  );

  return (
    <View style={[styles.map, style]}>
      <div ref={containerRef} style={StyleSheet.flatten(styles.canvas)} />
      {error && (
        <View style={styles.error}>
          <Text style={styles.errorText}>
            {error === 'auth'
              ? 'Google Maps rejected the API key. Enable the Maps JavaScript API for it.'
              : 'Google Maps could not be loaded.'}
          </Text>
        </View>
      )}
      {map && (
        <MapContext.Provider value={map}>
          {children}
          {showsUserLocation && userLocation && (
            <Marker
              coordinate={userLocation}
              anchor={{x: 0.5, y: 0.5}}
              zIndex={1000}>
              <View style={styles.userDot} />
            </Marker>
          )}
        </MapContext.Provider>
      )}
    </View>
  );
});

// Projection access for point <-> coordinate conversions.
const overlayProjection = map => {
  if (!map) {
    return null;
  }
  if (!map.__pompiProjection) {
    const helper = new window.google.maps.OverlayView();
    helper.onAdd = () => {};
    helper.draw = () => {};
    helper.onRemove = () => {};
    helper.setMap(map);
    map.__pompiProjection = helper;
  }
  return map.__pompiProjection.getProjection();
};

const markerCoords = map => Array.from(map?.__pompiMarkers?.values() || []);

// --- Marker -------------------------------------------------------------------

// An OverlayView that hosts a DOM node at a coordinate; React renders into it.
let HtmlOverlay = null;
const getHtmlOverlay = () => {
  if (HtmlOverlay) {
    return HtmlOverlay;
  }
  const {OverlayView, LatLng} = window.google.maps;
  HtmlOverlay = class extends OverlayView {
    constructor(element) {
      super();
      this.element = element;
      this.position = null;
      this.anchor = {x: 0.5, y: 1};
    }
    onAdd() {
      OverlayView.preventMapHitsAndGesturesFrom(this.element);
      this.getPanes().overlayMouseTarget.appendChild(this.element);
      // Google doesn't redraw overlays when the map div is resized.
      this.boundsListener = this.getMap().addListener('bounds_changed', () =>
        this.draw(),
      );
    }
    draw() {
      const projection = this.getProjection();
      if (!projection || !this.position) {
        return;
      }
      const p = projection.fromLatLngToDivPixel(new LatLng(this.position));
      if (!p) {
        return;
      }
      Object.assign(this.element.style, {
        left: `${p.x}px`,
        top: `${p.y}px`,
        transform: `translate(${-this.anchor.x * 100}%, ${
          -this.anchor.y * 100
        }%)`,
      });
    }
    onRemove() {
      this.boundsListener?.remove();
      this.element.remove();
    }
    update(position, anchor) {
      this.position = position;
      this.anchor = anchor;
      this.draw();
    }
  };
  return HtmlOverlay;
};

const splitCallout = children => {
  let callout = null;
  const rest = [];
  React.Children.forEach(children, child => {
    if (child && child.type === Callout) {
      callout = child;
    } else {
      rest.push(child);
    }
  });
  return {callout, content: rest};
};

export const Marker = forwardRef(function Marker(props, ref) {
  const {
    coordinate,
    children,
    title,
    description,
    anchor,
    zIndex,
    opacity = 1,
    onCalloutPress,
    identifier,
  } = props;
  const map = useContext(MapContext);
  const [calloutOpen, setCalloutOpen] = useState(false);
  const {callout, content} = splitCallout(children);
  const hasCustomView = content.some(Boolean);
  const hasCallout = Boolean(callout || title || description);
  const valid = isValidCoord(coordinate);
  const position = valid ? toLatLng(coordinate) : null;

  const handlers = useRef(props);
  handlers.current = props;

  useImperativeHandle(ref, () => ({
    showCallout: () => setCalloutOpen(true),
    hideCallout: () => setCalloutOpen(false),
    redraw: () => {},
    redrawCallout: () => {},
    animateMarkerToCoordinate: () => {},
  }));

  // Close the callout when the map itself is tapped, like on a phone.
  useEffect(() => {
    if (!map || !hasCallout) {
      return;
    }
    const l = map.addListener('click', () => setCalloutOpen(false));
    return () => l.remove();
  }, [map, hasCallout]);

  // Track markers for fitToElements.
  const [key] = useState(() => identifier || Symbol('marker'));
  useEffect(() => {
    if (!map || !valid) {
      return;
    }
    map.__pompiMarkers = map.__pompiMarkers || new Map();
    map.__pompiMarkers.set(key, coordinate);
    return () => map.__pompiMarkers.delete(key);
  }, [map, valid, coordinate, key]);

  const press = () => {
    if (hasCallout) {
      setCalloutOpen(open => !open);
    }
    handlers.current.onPress?.({
      stopPropagation: () => {},
      nativeEvent: {coordinate, action: 'marker-press', id: identifier},
    });
  };

  // Default Google pin when there's no custom view.
  const pinRef = useRef(null);
  useEffect(() => {
    if (!map || hasCustomView || !position) {
      return;
    }
    const pin = new window.google.maps.Marker({map});
    const l = pin.addListener('click', () => pressRef.current());
    pinRef.current = pin;
    return () => {
      l.remove();
      pin.setMap(null);
      pinRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, hasCustomView, valid]);
  const pressRef = useRef(press);
  pressRef.current = press;
  useEffect(() => {
    pinRef.current?.setOptions({position, title, zIndex, opacity});
  });

  // Custom view (and the callout, for both kinds) lives in an HTML overlay.
  const element = useMemo(() => {
    const el = document.createElement('div');
    el.style.position = 'absolute';
    el.style.cursor = 'pointer';
    return el;
  }, []);
  const overlayRef = useRef(null);
  const needsOverlay = hasCustomView || calloutOpen;
  // A default pin's callout sits above the pin, which is ~40px tall.
  const placement = useRef(null);
  placement.current = {
    position,
    anchor: hasCustomView ? anchor || {x: 0.5, y: 1} : {x: 0.5, y: 1},
  };
  useEffect(() => {
    if (!map || !needsOverlay) {
      return;
    }
    const Overlay = getHtmlOverlay();
    const overlay = new Overlay(element);
    overlayRef.current = overlay;
    overlay.update(placement.current.position, placement.current.anchor);
    overlay.setMap(map);
    return () => {
      overlay.setMap(null);
      overlayRef.current = null;
    };
  }, [map, needsOverlay, element]);
  useLayoutEffect(() => {
    element.style.zIndex = String(calloutOpen ? 100000 : zIndex ?? 0);
    element.style.opacity = String(opacity);
    overlayRef.current?.update(
      placement.current.position,
      placement.current.anchor,
    );
  });

  if (!map || !valid || !needsOverlay) {
    return null;
  }

  const calloutView = calloutOpen && (
    <View
      style={[styles.calloutWrap, !hasCustomView && styles.calloutAbovePin]}>
      {callout ? (
        React.cloneElement(callout, {
          __onPress: () => {
            callout.props.onPress?.({nativeEvent: {coordinate}});
            onCalloutPress?.({nativeEvent: {coordinate}});
          },
        })
      ) : (
        <Callout
          __onPress={() => onCalloutPress?.({nativeEvent: {coordinate}})}>
          <View style={styles.defaultCallout}>
            {!!title && <Text style={styles.calloutTitle}>{title}</Text>}
            {!!description && (
              <Text style={styles.calloutDescription}>{description}</Text>
            )}
          </View>
        </Callout>
      )}
    </View>
  );

  return createPortal(
    <View>
      {calloutView}
      {hasCustomView && (
        <Pressable
          onPress={press}
          accessibilityRole="button"
          accessibilityLabel={title}>
          <View pointerEvents="box-only">{content}</View>
        </Pressable>
      )}
    </View>,
    element,
  );
});

// Rendered by Marker inside its overlay; on its own it renders nothing.
export function Callout({children, tooltip, style, __onPress}) {
  if (!__onPress) {
    return null;
  }
  return (
    <Pressable
      onPress={__onPress}
      style={[!tooltip && styles.calloutBubble, style]}>
      {children}
    </Pressable>
  );
}

export function CalloutSubview({children, onPress, style}) {
  return (
    <Pressable onPress={e => onPress?.(e)} style={style}>
      {children}
    </Pressable>
  );
}

// --- Shapes ---------------------------------------------------------------------

const useShape = (create, options, onPress) => {
  const map = useContext(MapContext);
  const shapeRef = useRef(null);
  const pressRef = useRef(onPress);
  pressRef.current = onPress;
  useEffect(() => {
    if (!map) {
      return;
    }
    const shape = create(window.google.maps);
    shape.setMap(map);
    const l = shape.addListener('click', e =>
      pressRef.current?.({
        nativeEvent: {coordinate: e.latLng && fromLatLng(e.latLng)},
      }),
    );
    shapeRef.current = shape;
    return () => {
      l.remove();
      shape.setMap(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);
  useEffect(() => {
    shapeRef.current?.setOptions(options);
  });
  return null;
};

export function Polyline({
  coordinates = [],
  strokeColor = '#000',
  strokeWidth = 1,
  zIndex,
  tappable,
  onPress,
}) {
  return useShape(
    maps => new maps.Polyline(),
    {
      path: coordinates.filter(isValidCoord).map(toLatLng),
      strokeColor,
      strokeOpacity: 1,
      strokeWeight: strokeWidth,
      zIndex,
      clickable: Boolean(tappable || onPress),
    },
    onPress,
  );
}

export function Polygon({
  coordinates = [],
  strokeColor = '#000',
  strokeWidth = 1,
  fillColor,
  zIndex,
  tappable,
  onPress,
}) {
  return useShape(
    maps => new maps.Polygon(),
    {
      paths: coordinates.filter(isValidCoord).map(toLatLng),
      strokeColor,
      strokeWeight: strokeWidth,
      fillColor,
      fillOpacity: fillColor ? 1 : 0,
      zIndex,
      clickable: Boolean(tappable || onPress),
    },
    onPress,
  );
}

export function Circle({
  center,
  radius,
  strokeColor = '#000',
  strokeWidth = 1,
  fillColor,
  zIndex,
  onPress,
}) {
  return useShape(
    maps => new maps.Circle(),
    {
      center: isValidCoord(center) ? toLatLng(center) : null,
      radius,
      strokeColor,
      strokeWeight: strokeWidth,
      fillColor,
      fillOpacity: fillColor ? 1 : 0,
      zIndex,
      clickable: Boolean(onPress),
    },
    onPress,
  );
}

export const Overlay = () => null;
export const Heatmap = () => null;
export const Geojson = () => null;
export const UrlTile = () => null;
export const LocalTile = () => null;
export const AnimatedRegion = class {
  constructor(region) {
    Object.assign(this, region);
  }
  timing(config) {
    return {
      start: cb => {
        Object.assign(this, config);
        cb?.({finished: true});
      },
    };
  }
};

const styles = StyleSheet.create({
  map: {overflow: 'hidden', backgroundColor: '#E5E3DF'},
  canvas: {position: 'absolute', top: 0, right: 0, bottom: 0, left: 0},
  error: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  errorText: {textAlign: 'center', color: '#475569', fontSize: 14},
  userDot: {
    width: 18,
    height: 18,
    borderRadius: 9,
    backgroundColor: '#1A73E8',
    borderWidth: 3,
    borderColor: '#FFFFFF',
    boxShadow:
      '0 0 0 6px rgba(26, 115, 232, 0.2), 0 1px 3px rgba(0, 0, 0, 0.3)',
  },
  // The callout floats above the marker it belongs to.
  calloutWrap: {
    position: 'absolute',
    bottom: '100%',
    left: '50%',
    transform: [{translateX: '-50%'}, {translateY: -6}],
    alignItems: 'center',
  },
  calloutAbovePin: {transform: [{translateX: '-50%'}, {translateY: -40}]},
  calloutBubble: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    padding: 8,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
  },
  defaultCallout: {minWidth: 120, maxWidth: 240},
  calloutTitle: {fontWeight: '600', fontSize: 14, color: '#0F172A'},
  calloutDescription: {fontSize: 12, color: '#64748B', marginTop: 2},
});

export default MapView;
export {MapView};
